import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { MessageCircleQuestion, Loader2 } from "lucide-react";
import { sharedApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { timeAgo } from "@/lib/format";

/**
 * Questions & Answers panel — buyers ask, sellers respond. Uses the
 * shared backend's `/classifieds/listings/:id/questions` endpoint if
 * available; degrades to an empty state when the endpoint returns 404 so
 * older environments don't break.
 */
export default function QuestionsAndAnswers({ listingId, sellerId }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [replyingId, setReplyingId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const isOwner = user && sellerId && (user.id === sellerId);

  const q = useQuery({
    queryKey: ["ad-questions", listingId],
    queryFn: async () => {
      try {
        const { data } = await sharedApi.get(`/classifieds/listings/${listingId}/questions`);
        return Array.isArray(data) ? data : data?.questions || [];
      } catch (err) {
        if (err?.response?.status === 404) return [];
        throw err;
      }
    },
    enabled: !!listingId,
    staleTime: 30_000,
  });

  const mut = useMutation({
    mutationFn: async () => {
      const { data } = await sharedApi.post(`/classifieds/listings/${listingId}/questions`, {
        question: text.trim(),
      });
      return data;
    },
    onSuccess: () => {
      toast.success("Question sent — the seller will be notified.");
      setText("");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["ad-questions", listingId] });
    },
    onError: (err) =>
      toast.error(err?.response?.data?.detail || "Couldn't send your question"),
  });

  // Seller-reply mutation (iter-227 mirror-form): the answer endpoint is
  // KEYED BY QUESTION ID, NOT nested under the listing. Payload is a
  // simple `{ answer }` string. Only surfaced to the listing owner via
  // the isOwner flag above.
  const answerMut = useMutation({
    mutationFn: async ({ qid, answer }) => {
      const { data } = await sharedApi.post(
        `/classifieds/questions/${qid}/answer`,
        { answer: answer.trim() }
      );
      return data;
    },
    onSuccess: () => {
      toast.success("Reply sent to the buyer.");
      setReplyingId(null);
      setReplyText("");
      qc.invalidateQueries({ queryKey: ["ad-questions", listingId] });
    },
    onError: (err) =>
      toast.error(err?.response?.data?.detail || "Couldn't send your reply"),
  });

  const items = q.data || [];

  const submit = (e) => {
    e.preventDefault();
    if (!user) {
      toast.info("Sign in to ask a question.");
      return;
    }
    if (!text.trim()) return;
    mut.mutate();
  };

  return (
    <section
      data-testid="ad-qa"
      className="mt-6 pt-6 border-t border-subtle"
    >
      <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-4">
        <h3 className="text-sm font-semibold text-slate-600 flex items-center gap-2">
          <MessageCircleQuestion className="w-4 h-4 text-[color:var(--brand-blue)]" />
          Questions &amp; Answers
        </h3>
        <div>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="ad-ask-question-btn"
            className="inline-flex items-center gap-2 rounded bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white font-bold px-4 py-2 text-sm transition-colors"
          >
            Ask a question
          </button>
          {open && (
            <form onSubmit={submit} className="mt-3 space-y-2">
              <textarea
                data-testid="ad-question-textarea"
                value={text}
                onChange={(e) => setText(e.target.value.slice(0, 500))}
                placeholder="Your question about this listing…"
                className="w-full min-h-24 rounded-xl border border-soft px-3 py-2 text-sm focus:border-[color:var(--brand-blue)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand-blue)]/20"
                maxLength={500}
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => { setOpen(false); setText(""); }}
                  className="rounded-full px-4 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={mut.isPending || !text.trim()}
                  data-testid="ad-question-submit"
                  className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white font-bold px-4 py-1.5 text-sm disabled:opacity-60 transition-colors"
                >
                  {mut.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Send
                </button>
              </div>
            </form>
          )}
          <div className="mt-4 space-y-3">
            {q.isLoading ? (
              <div className="text-sm text-slate-500">Loading questions…</div>
            ) : items.length === 0 ? (
              <div data-testid="ad-qa-empty" className="text-sm text-slate-500">
                No questions have been asked!
              </div>
            ) : (
              items.map((qa) => (
                <article
                  key={qa.id}
                  data-testid={`ad-qa-item-${qa.id}`}
                  className="rounded-xl bg-slate-50 border border-subtle p-3"
                >
                  <div className="text-sm text-slate-800">{qa.question}</div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    {qa.asker_name || "A buyer"} · {timeAgo(qa.created_at)}
                  </div>
                  {qa.answer ? (
                    <div className="mt-2 pt-2 border-t border-soft">
                      <div className="text-xs font-bold text-[color:var(--brand-blue)] uppercase tracking-wide">
                        Seller reply
                      </div>
                      <div className="text-sm text-slate-800 mt-0.5">{qa.answer}</div>
                    </div>
                  ) : isOwner ? (
                    <div className="mt-2 pt-2 border-t border-soft">
                      {replyingId === qa.id ? (
                        <div className="space-y-2">
                          <textarea
                            data-testid={`ad-qa-reply-textarea-${qa.id}`}
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value.slice(0, 500))}
                            className="w-full min-h-20 rounded-xl border border-soft px-3 py-2 text-sm focus:border-[color:var(--brand-blue)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand-blue)]/20"
                            placeholder="Your reply to this buyer…"
                            maxLength={500}
                          />
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => { setReplyingId(null); setReplyText(""); }}
                              className="text-xs font-semibold text-slate-600 hover:underline"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={() => answerMut.mutate({ qid: qa.id, answer: replyText })}
                              disabled={answerMut.isPending || !replyText.trim()}
                              data-testid={`ad-qa-reply-submit-${qa.id}`}
                              className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white font-bold px-3 py-1 text-xs disabled:opacity-60 transition-colors"
                            >
                              Send reply
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => { setReplyingId(qa.id); setReplyText(""); }}
                          data-testid={`ad-qa-reply-btn-${qa.id}`}
                          className="text-xs font-semibold text-[color:var(--brand-blue)] hover:underline"
                        >
                          Reply as seller
                        </button>
                      )}
                    </div>
                  ) : null}
                </article>
              ))
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
